import { memo } from "react";
import { BaseButton } from "../../../components/Button/BaseButton.tsx";
import { BaseClickableIcon } from "../../../components/Icon/BaseClickableIcon.tsx";
import { Icon } from "../../../components/Icon/Icon.tsx";
import { useDownload } from "../../../hooks/useDownload.ts";
import { useAppMutation } from "../../../http/useAppMutation.ts";
import { notifier } from "../../../utils/notifier.tsx";
import { RESOURCE_TYPE_TO_EXTENSION } from "../../Board/constants.ts";
import type { Content } from "../../Board/types.ts";
import { FROM_PURCHASED_ASSET_PRESET } from "../../boardState/constants.ts";
import { ActionContentTile } from "../../components/ActionContentTile.tsx";
import { useRedirectToBoard } from "../../hooks/useRedirectToBoard.ts";
import { useWorkspaceUuid } from "../../hooks/useWorkspaceUuid.ts";

export const PurchasedContentTile = memo(
  ({ content }: { content: Content }) => {
    const { mutate: download, isLoading: isDownloading } = useDownload();
    const redirectToBoard = useRedirectToBoard();
    const workspaceUuid = useWorkspaceUuid();
    const { mutate: getOrCreateAssetFromContent } = useAppMutation({
      path: "contents/get-or-create-asset-from-content",
      invalidate: [],
      onSuccess: (response: {
        data: { asset_uuid: string; board_uuid: string };
      }) => {
        redirectToBoard({
          uuid: response.data.board_uuid,
          boardStatePreset: FROM_PURCHASED_ASSET_PRESET,
          baseBoardState: {
            selectedAssetUuid: response.data.asset_uuid,
          },
        });
      },
    }).mutation;

    return (
      <ActionContentTile
        content={content}
        onClick={() => {
          getOrCreateAssetFromContent({
            uuid: content.uuid,
            workspace_uuid: workspaceUuid,
          });
        }}
        bottomRightElement={
          <div className="flex-row gap-200 invisible group-hover:visible">
            <BaseClickableIcon
              className="z-10 tile-action"
              name="Download"
              size="sm"
              isLoading={isDownloading}
              onClick={() => {
                if (content.url) {
                  download({
                    items: [
                      {
                        url: content.url,
                        filename: `${content.uuid}.${
                          RESOURCE_TYPE_TO_EXTENSION[content.resource_type]
                        }`,
                        type: content.resource_type,
                      },
                    ],
                    onError: () => {
                      notifier.error("Error downloading content");
                    },
                  });
                } else {
                  notifier.error("Error downloading content");
                }
              }}
            />
            <BaseButton
              className="z-10 tile-action hover:brightness-95 active:brightness-90 flex-row-center gap-200"
              onClick={() =>
                getOrCreateAssetFromContent({
                  uuid: content.uuid,
                  workspace_uuid: workspaceUuid,
                })
              }
            >
              <Icon name="PencilLine" size="sm" />
              Edit
            </BaseButton>
          </div>
        }
      />
    );
  },
);
PurchasedContentTile.displayName = "PurchasedContentTile";
